/**
 * Portal-only hooks: session/auth, store list + switching, billing history.
 * These talk to the portal endpoints directly (not the /api → /portal rewrite),
 * so they're used only when running as the standalone dashboard.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { IS_PORTAL, getActiveStoreId, setActiveStoreId } from '../lib/mode';
import type {
  BillingHistoryItem,
  ConnectedStore,
  PortalUser,
  PortfolioStats,
  StoresResponse,
} from '../types';

export const portalKeys = {
  session: ['portal', 'session'] as const,
  stores: ['portal', 'stores'] as const,
  billingHistory: ['portal', 'billing-history'] as const,
  portfolio: ['portal', 'portfolio'] as const,
};

/** Plans that can hold more than one store → the portfolio view is relevant. */
const MULTI_STORE_PLANS = new Set(['growth', 'pro']);

/** True when any of the user's connected stores is on a multi-store plan. */
export function hasPortfolioAccess(stores: ConnectedStore[] | undefined): boolean {
  return Boolean(stores?.some((s) => MULTI_STORE_PLANS.has(s.plan) && !s.uninstalled));
}

/** Combined "all stores" view (Growth/Pro). Disabled when not eligible. */
export function usePortfolio(enabled: boolean) {
  return useQuery({
    queryKey: portalKeys.portfolio,
    enabled: IS_PORTAL && enabled,
    queryFn: () => api.get<PortfolioStats>('/portal/portfolio'),
  });
}

/** Current signed-in portal user (null when not authenticated). */
export function useSession() {
  return useQuery({
    queryKey: portalKeys.session,
    enabled: IS_PORTAL,
    retry: false,
    queryFn: async () => {
      try {
        return await api.get<PortalUser>('/auth/portal/me');
      } catch {
        return null; // not signed in
      }
    },
  });
}

export function useRequestMagicLink() {
  return useMutation({
    mutationFn: (email: string) =>
      api.post<{ ok: boolean; message: string }>('/auth/portal/request-link', { email }),
  });
}

export function useVerifyMagicLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => api.post<PortalUser>('/auth/portal/verify', { token }),
    onSuccess: () => qc.invalidateQueries({ queryKey: portalKeys.session }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/auth/portal/logout'),
    onSuccess: () => {
      setActiveStoreId(null);
      qc.clear();
    },
  });
}

/** The user's connected stores + plan-based connection cap. */
export function useStores() {
  return useQuery({
    queryKey: portalKeys.stores,
    enabled: IS_PORTAL,
    queryFn: () => api.get<StoresResponse>('/portal/stores'),
  });
}

/** Start connecting a Shopify store → returns the Shopify authorize URL. */
export function useConnectStore() {
  return useMutation({
    mutationFn: (shop: string) =>
      api.post<{ authorizeUrl: string }>('/auth/portal/connect-store', { shop }),
  });
}

/** Switch the active store (persists locally + server-side last-selected). */
export function useSelectStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (storeId: string) => {
      setActiveStoreId(storeId);
      await api.post('/portal/stores/select', { storeId });
      return storeId;
    },
    onSuccess: () => {
      // Re-fetch everything for the newly active store.
      qc.invalidateQueries();
    },
  });
}

export function useDisconnectStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (storeId: string) => api.del(`/portal/stores/${storeId}`),
    onSuccess: (_d, storeId) => {
      if (getActiveStoreId() === storeId) setActiveStoreId(null);
      qc.invalidateQueries({ queryKey: portalKeys.stores });
    },
  });
}

/** Billing / payment history for the active store. */
export function useBillingHistory() {
  return useQuery({
    queryKey: portalKeys.billingHistory,
    enabled: IS_PORTAL,
    queryFn: () => api.get<{ events: BillingHistoryItem[] }>('/api/billing/history'),
  });
}

/** Convenience: the currently-active store object from the list. */
export function activeStoreFrom(stores: ConnectedStore[] | undefined): ConnectedStore | null {
  if (!stores || stores.length === 0) return null;
  const active = getActiveStoreId();
  return stores.find((s) => s.id === active) ?? stores[0] ?? null;
}
